'use strict'

// HELPERS

// Cache for memoizing getBrazeProduct transformations to reduce repeated work.
// Uses WeakMap so products can be garbage collected when no longer referenced.
const productCache = new WeakMap()

import { getConfig } from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import { logBrazeEvent } from '../integrations/braze/braze-manager'
import { productUrlBuilder, absoluteUrl } from './url'
import { getSiteEnvVarValue } from './site-config'
import { resolveBrandFromUrl } from '../contexts/brand-context'

const BRAZE_SOURCE = 'web'

/**
 * Determines whether the Braze integration is enabled via site configuration.
 * Uses `getSiteEnvVarValue` so both the flat `IS_BRAZE_ENABLED = 'true'` shape
 * and the multi-site shape (`{DynamiteGarageCA: true, ...}`) are supported.
 *
 * @returns {boolean} True when Braze is enabled.
 */
const isBrazeEnabled = () => {
    const {app} = getConfig() || {}
    if (!app) return false
    const siteId = app.commerceAPI?.parameters?.siteId
    return getSiteEnvVarValue(app, 'IS_BRAZE_ENABLED', siteId) === 'true'
}

/**
 * Resolves the best available image URL for a product or product line item.
 *
 * @param {Object} product - The product or basket product item object.
 * @returns {string|undefined} An absolute or CDN image URL, or undefined when none is found.
 */
const getProductImageUrl = (product) => {
    if (!product) return undefined

    const imageGroups = product.representedProduct?.imageGroups || product.imageGroups || []
    const color = product.representedProduct?.c_color
        || product.c_color
        || product.variationGroups?.[0]?.variationValues?.color

    const matchesColor = (group) =>
        group.variationAttributes?.some(
            (attribute) =>
                attribute.id === 'color' &&
                attribute.values?.some((value) => value.value === color)
        )

    const preferredGroup =
        (color &&
            imageGroups.find(
                (group) => group.viewType !== 'swatch' && matchesColor(group) && group?.images?.length
            ))

    const image = preferredGroup?.images?.[0]

    return image?.link || product.image?.link || undefined
}

/**
 * Builds an absolute product detail page URL for a given product.
 *
 * @param {Object} product - The product or basket product item object.
 * @returns {string|undefined} The absolute product URL, or undefined when it cannot be derived.
 */
const getProductUrl = (product) => {
    if (!product) return undefined

    try {
        return absoluteUrl(productUrlBuilder(product))
    } catch (error) {
        return product.c_relativeUrl || undefined
    }
}

/**
 * Extracts the category breadcrumb metadata (item_category, item_category2, item_category3)
 * from a product's custom category data.
 *
 * @param {Object} product - The product or basket product item object.
 * @returns {Object} An object with up to three item_category* keys.
 */
const getCategoryMetadata = (product) => {
    const metadata = {}
    const source = product.representedProduct?.c_categories || product.c_categories

    if (!Array.isArray(source) || !source.length) return metadata

    const categories = [...source].reverse()

    if (categories[0].length <= 1) {
        categories.shift()
    }

    if (categories.length > 0) {
        metadata.item_category = categories[0]

        if (categories.length > 1) {
            metadata.item_category2 = categories[1]

            if (categories.length > 2) {
                metadata.item_category3 = categories[2]
            }
        }
    }

    return metadata
}

/**
 * Resolves the SKU for the first orderable size of a product.
 *
 * @param {Object} product - The product object containing variationAttributes and variants.
 * @returns {Object|undefined} An object with the matched size value and variant productId (sku),
 *   or undefined when no orderable size/variant can be resolved.
 */
const getFirstOrderableSizeSku = (product) => {
    if (!product) return undefined

    const sizeAttribute = product.variationAttributes?.find((attribute) => attribute.id === 'size')
    const firstOrderableSize = sizeAttribute?.values?.find((value) => value.orderable)

    if (!firstOrderableSize) return undefined

    const matchingVariant = product.variants?.find(
        (variant) => variant.variationValues?.size === firstOrderableSize.value
    )

    if (!matchingVariant) return undefined

    return {
        size: firstOrderableSize.value,
        sku: matchingVariant.productId
    }
}

/**
 * Resolves the current storefront brand name (e.g. "Garage", "Dynamite") from the
 * hostname. Used as a fallback for item_brand because product search hits on PLPs
 * and quick view do not carry brand attributes (see product-tile: `product.brand`
 * is absent on search hits), which left item_brand undefined on the Dynamite site.
 *
 * @returns {string|undefined} The capitalized brand name, or undefined when it cannot be resolved.
 */
const getSiteBrandName = () => {
    if (typeof window === 'undefined') return undefined

    const brandId = resolveBrandFromUrl(window.location?.hostname)

    if (!brandId) return undefined

    return brandId.charAt(0).toUpperCase() + brandId.slice(1)
}

/**
 * Constructs a Braze-compatible product object from a product entity.
 * Memoized to prevent redundant transformations for the same product.
 *
 * @param {Object} product - The product object containing details such as id, name, price, variants, and categories.
 * @param {Object} [options] - Optional extras.
 * @param {boolean} [options.includeQuantity] - Whether to include the quantity field (cart/checkout/purchase events).
 * @param {string} [options.itemListId] - Optional item_list_id added to the product metadata (wishlist events).
 * @returns {Object|undefined} An object representing the product in Braze's expected format, or undefined when no product is provided.
 */
const getBrazeProduct = (product, options = {}) => {
    if (!product) return undefined

    const { includeQuantity = false, itemListId } = options

    // Check cache - WeakMap uses product object reference as key
    if (productCache.has(product)) {
        const cached = productCache.get(product)

        if (cached.includeQuantity === includeQuantity && cached.itemListId === itemListId) {
            return cached.result
        }
    }

    const pid = 'productId' in product ? product.productId : product.id
    const name = 'productName' in product ? product.productName : product.name

    const firstOrderable = getFirstOrderableSizeSku(product)

    const sku = product.representedProduct?.id ?? firstOrderable?.sku ?? product.variants?.[0]?.productId
    const skuSize = firstOrderable?.size ?? product.variants?.[0]?.variationValues?.size

    let variationGroupId = null

    if (product.type?.variationGroup || product?.productType?.variationGroup) {
        variationGroupId = pid
    } else if (product.representedProduct || product.c_variationGroup) {
        variationGroupId = product.c_variationGroupId || product.c_variationGroup
    } else if (product.variationGroups?.length) {
        variationGroupId = product.variationGroups[0].productId
    }

    const metadata = getCategoryMetadata(product)

    metadata.item_brand =
        product.c_brand ||
        product.brand ||
        product.c_gtmBrand ||
        product.representedProduct?.c_gtmBrand ||
        getSiteBrandName()
    metadata.item_variant = product.representedProduct?.c_refinementColor || product.c_refinementColor
    metadata.item_size = product.c_size || product?.representedProduct?.c_size || skuSize

    if (itemListId) {
        metadata.item_list_id = itemListId
    }

    const price = Number(product.basePrice ?? product.price)

    const brazeProduct = {
        product_id: sku || pid,
        product_name: name,
        variant_id: variationGroupId,
        price: Number.isNaN(price) ? undefined : price,
        image_url: getProductImageUrl(product),
        product_url: getProductUrl(product),
        metadata
    }

    if (includeQuantity) {
        brazeProduct.quantity = product.quantity || 1
    }

    // Cache the result
    productCache.set(product, { includeQuantity, itemListId, result: brazeProduct })

    return brazeProduct
}

/**
 * Maps a list of products into an array of Braze-compatible product objects,
 * skipping any null/undefined entries.
 *
 * @param {Array<Object>} list - An array of product objects.
 * @param {Object} [options] - Options forwarded to getBrazeProduct (e.g. includeQuantity).
 * @returns {Array<Object>} An array of Braze product objects.
 */
const getBrazeProducts = (list, options = {}) => {
    if (!list?.length) return []

    return list.map((product) => getBrazeProduct(product, options)).filter(Boolean)
}

/**
 * Resolves the product currency, preferring the product-level value and falling back to the basket currency.
 *
 * @param {Object} product - The product object.
 * @param {string} [basketCurrency] - The currency of the basket.
 * @returns {string|undefined} The resolved currency code.
 */
const getCurrency = (product, basketCurrency) =>
    product?.currency || product?.c_currency || basketCurrency

// E-COMMERCE EVENTS

/**
 * Logs a Braze 'ecommerce.product_viewed' event for a given product.
 *
 * @param {Object} product - The product being viewed.
 * @returns {void}
 */
function sendBrazeViewItemEvent(product) {
    if (!isBrazeEnabled()) return
    if (!product) return

    logBrazeEvent('ecommerce.product_viewed', {
        currency: getCurrency(product),
        source: BRAZE_SOURCE,
        products: [getBrazeProduct(product)]
    })
}

/**
 * Logs a Braze 'ecommerce.cart_updated' event when an item is added to, removed from, or replaced in the cart.
 *
 * @param {Object} basket - The current basket, used for cart_id, currency, and total_value.
 * @param {Object} product - The product that was added/removed/replaced.
 * @param {string} action - The cart action: 'add', 'remove', or 'replace'.
 * @returns {void}
 */
function sendBrazeCartEvent(basket, product, action) {
    if (!isBrazeEnabled()) return
    if (!product) return

    logBrazeEvent('ecommerce.cart_updated', {
        cart_id: basket?.basketId,
        action,
        currency: getCurrency(product, basket?.currency),
        source: BRAZE_SOURCE,
        total_value: basket?.orderTotal ?? basket?.productTotal,
        products: [getBrazeProduct(product, { includeQuantity: true })]
    })
}

/**
 * Logs a Braze 'ecommerce.checkout_started' event with the current basket contents.
 *
 * @param {Object} basket - The current basket object.
 * @param {Object<string, Object>} [products] - Enriched product details keyed by product id.
 * @returns {void}
 */
function sendBrazeBeginCheckoutEvent(basket, products) {
    if (!isBrazeEnabled()) return
    if (!basket) return

    const items = (basket.productItems || []).map((item) => {
        const details = products?.[item.productId]

        return details ? { ...details, quantity: item.quantity } : item
    })

    logBrazeEvent('ecommerce.checkout_started', {
        checkout_id: basket.basketId,
        currency: basket.currency,
        source: BRAZE_SOURCE,
        total_value: basket.orderTotal,
        products: getBrazeProducts(items, { includeQuantity: true })
    })
}

/**
 * Logs a Braze 'ecommerce.order_placed' event with the completed order's contents.
 *
 * @param {Object} order - The order object containing purchase information.
 * @param {Object} [products] - Optional id-keyed map of enriched product details to merge into the order line items.
 * @returns {void}
 */
function sendBrazePurchaseEvent(order, products) {
    if (!isBrazeEnabled()) return
    if (!order) return

    const items = (order.productItems || []).map((item) => {
        const details = products?.[item.productId]

        return details ? { ...details, quantity: item.quantity } : item
    })

    logBrazeEvent('ecommerce.order_placed', {
        order_id: order.orderNo,
        currency: order.currency,
        source: BRAZE_SOURCE,
        total_value: order.orderTotal,
        products: getBrazeProducts(items, { includeQuantity: true })
    })
}

// CUSTOM EVENTS

/**
 * Logs a Braze wishlist event for adding or removing a product from the wishlist.
 *
 * @param {Object} product - The product object being added/removed.
 * @param {boolean} isRemoval - Whether the event is a removal (true) or an addition (false).
 * @param {Object} [category] - Optional category context used to populate item_list_id in the product metadata.
 * @returns {void}
 */
function sendBrazeWishlistEvent(product, isRemoval, categoryId = 'primary category not assigned') {
    if (!isBrazeEnabled()) return
    if (!product) return

    const eventName = isRemoval ? 'remove_from_wishlist' : 'add_to_wishlist'

    logBrazeEvent(eventName, {
        currency: getCurrency(product),
        source: BRAZE_SOURCE,
        products: [getBrazeProduct(product, { itemListId: categoryId })]
    })
}

/**
 * Logs a Braze 'view_item_list' event when a user views a category page or a recommended list of items.
 *
 * @param {Object} category - The list/category object containing the id and name.
 * @returns {void}
 */
function sendBrazeViewItemListEvent(category) {
    if (!isBrazeEnabled()) return
    if (!category) return

    logBrazeEvent('view_item_list', {
        item_list_id: category?.id,
        item_list_name: category?.name,
        source: BRAZE_SOURCE
    })
}

/**
 * Logs a Braze 'view_search_results' event when a user searches and views the resulting items.
 *
 * @param {string} searchTerm - The search term entered by the user.
 * @returns {void}
 */
function sendBrazeViewSearchResultsEvent(searchTerm) {
    if (!isBrazeEnabled()) return

    logBrazeEvent('view_search_results', {
        search_term: searchTerm,
        source: BRAZE_SOURCE
    })
}

export {
    sendBrazeViewItemEvent,
    sendBrazeCartEvent,
    sendBrazeBeginCheckoutEvent,
    sendBrazePurchaseEvent,
    sendBrazeWishlistEvent,
    sendBrazeViewItemListEvent,
    sendBrazeViewSearchResultsEvent
}
